import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Wallet, TrendingUp, DollarSign, CreditCard, ArrowUpRight, Award } from "lucide-react";
import axios from "axios";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mock earnings data
const monthlyEarnings = [
  { month: "Jul", earnings: 2800000, payouts: 2500000 },
  { month: "Aug", earnings: 3100000, payouts: 2750000 },
  { month: "Sep", earnings: 3400000, payouts: 3050000 },
  { month: "Oct", earnings: 3800000, payouts: 3400000 },
  { month: "Nov", earnings: 4200000, payouts: 3800000 },
  { month: "Dec", earnings: 4500000, payouts: 4100000 },
];

const earningsByService = [
  { service: "Home Visit", amount: 2100000 },
  { service: "Clinic Session", amount: 1800000 },
  { service: "Teleconsult", amount: 400000 },
  { service: "Assessment", amount: 200000 },
];

export default function AdminEarnings() {
  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/admin/therapists`, { withCredentials: true });
        setTherapists(response.data);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const totalEarnings = therapists.reduce((sum, t) => sum + (t.total_earnings || 0), 0);
  const totalMonthly = therapists.reduce((sum, t) => sum + (t.monthly_earnings || 0), 0);
  const topEarners = [...therapists].sort((a, b) => (b.total_earnings || 0) - (a.total_earnings || 0)).slice(0, 10);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-earnings">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center">
                <Wallet className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Platform Earnings</p>
                <p className="text-2xl font-heading font-bold text-slate-900">
                  RM {(totalEarnings / 1000).toFixed(0)}K
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">This Month</p>
                <p className="text-2xl font-heading font-bold text-slate-900">
                  RM {(totalMonthly / 1000).toFixed(0)}K
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Avg. per Therapist</p>
                <p className="text-2xl font-heading font-bold text-slate-900">
                  RM {therapists.length > 0 ? Math.round(totalMonthly / therapists.length).toLocaleString() : 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center">
                <Award className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Incentives Paid</p>
                <p className="text-2xl font-heading font-bold text-slate-900">RM 85K</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Earnings Trend */}
        <Card className="chart-container">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Earnings Trend (RM)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={monthlyEarnings}>
                <defs>
                  <linearGradient id="earningsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F40d07" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#F40d07" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`} />
                <Tooltip 
                  formatter={(value) => [`RM ${value.toLocaleString()}`, '']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="earnings" 
                  stroke="#F40d07" 
                  strokeWidth={2}
                  fill="url(#earningsGrad)" 
                  name="Earnings"
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Earnings by Service */}
        <Card className="chart-container">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Earnings by Service Type</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={earningsByService} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                <XAxis type="number" tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`} />
                <YAxis type="category" dataKey="service" width={100} />
                <Tooltip 
                  formatter={(value) => [`RM ${value.toLocaleString()}`, 'Revenue']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
                <Bar dataKey="amount" fill="#F40d07" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Top Earners Leaderboard */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-500" />
            Top Earners Leaderboard
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Rank</TableHead>
                <TableHead>Therapist</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Monthly Earnings</TableHead>
                <TableHead>Total Earnings</TableHead>
                <TableHead>Sessions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {topEarners.map((th, i) => (
                <TableRow key={th.therapist_id}>
                  <TableCell>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      i === 0 ? 'bg-amber-100 text-amber-700' :
                      i === 1 ? 'bg-slate-100 text-slate-600' :
                      i === 2 ? 'bg-orange-100 text-orange-700' :
                      'bg-slate-50 text-slate-500'
                    }`}>
                      {i + 1}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={th.picture} />
                        <AvatarFallback className="bg-primary text-white text-sm">
                          {th.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium">{th.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`tier-${th.tier}`}>{th.tier}</Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-slate-600">{th.role_type}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 font-medium text-emerald-600">
                      <ArrowUpRight className="w-4 h-4" />
                      RM {th.monthly_earnings?.toLocaleString()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-bold">RM {th.total_earnings?.toLocaleString()}</span>
                  </TableCell>
                  <TableCell>{th.total_sessions}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
